import type { ValueConstructor } from './DataFlow';
import type { AsyncFunctor, NamedAsyncFunctor } from './Functor';
import type { Type } from './Type';
import type {
  Graph,
  IValue,
  IValueCalculator,
  TypeCollection,
  TypedValueCollection,
  VertexDescriptor,
  VertexDescriptorCollection
} from './Graph';
import { TypedValue } from './Graph';
import { requireEqualPrototypes } from './Utils';

export class UndefinedValueError extends Error {
  public constructor() {
    super('Undefined value detected.');
    Object.setPrototypeOf(this, UndefinedValueError.prototype);
  }
}

export class InvalidNameError extends Error {
  public constructor() {
    super('Invalid name provided.');
    Object.setPrototypeOf(this, InvalidNameError.prototype);
  }
}

export class DuplicateNameError extends Error {
  public constructor() {
    super('Duplicate name provided.');
    Object.setPrototypeOf(this, DuplicateNameError.prototype);
  }
}

export class UnknownNameError extends Error {
  public constructor(name: string) {
    super('Unknown name provided: [' + name + '].');
    Object.setPrototypeOf(this, UnknownNameError.prototype);
  }
}

export class CycleDetectedError extends Error {
  public constructor() {
    super('Cycle detected.');
    Object.setPrototypeOf(this, CycleDetectedError.prototype);
  }
}

export class InputValueCalculator implements IValueCalculator {
  private _value: TypedValue;

  public readonly inpProto: TypeCollection;

  public readonly resProto: Type;

  public constructor(type: Type, value?: TypedValue) {
    if (value) {
      requireEqualPrototypes([type], [value.type]);
      this._value = value;
    } else {
      this._value = new TypedValue(type);
    }
    this.inpProto = [];
    this.resProto = type;
  }

  public setValue(value: TypedValue): void {
    requireEqualPrototypes([this.resProto], [value.type]);
    this._value = value;
  }

  public invokeAsync(inputs: TypedValueCollection): Promise<TypedValue | undefined> {
    requireEqualPrototypes(
      this.inpProto,
      inputs.map((value: TypedValue): Type => {
        return value.type;
      })
    );
    return Promise.resolve(this._value);
  }
}

export class FunctorValueCalculator implements IValueCalculator {
  private _functor: AsyncFunctor;

  public readonly name: string;

  public readonly inpProto: TypeCollection;

  public readonly resProto: Type;

  public constructor(functor: NamedAsyncFunctor, inpProto: TypeCollection, resProto: Type) {
    this._functor = functor.func;
    this.name = functor.name;
    this.inpProto = inpProto;
    this.resProto = resProto;
  }

  private async _invokeValuesAsync(...values: Array<IValue | undefined>): Promise<IValue | undefined> {
    try {
      if (values.indexOf(undefined) !== -1) {
        return undefined;
      }
      return await this._functor(...values);
    } catch (e: unknown) {
      if (e instanceof Error) {
        console.warn('EXCEPTION during functor calculation: [' + e.message + ']');
      } else {
        console.warn('EXCEPTION during functor calculation.');
      }
      return undefined;
    }
  }

  public async invokeAsync(inputs: TypedValueCollection): Promise<TypedValue | undefined> {
    requireEqualPrototypes(
      this.inpProto,
      inputs.map((value: TypedValue): Type => {
        return value.type;
      })
    );
    const values = inputs.map((value: TypedValue): IValue | undefined => {
      return value.value;
    });
    const value = await this._invokeValuesAsync(...values);
    if (!value) {
      return undefined;
    }
    const type = value.type;
    requireEqualPrototypes([this.resProto], [type]);
    return new TypedValue(type, value);
  }
}

// Export for Unit Testing
export class DataFlowBuilderNamingProxy {
  private _builder: DataFlowBuilder;

  private _name: string;

  public constructor(builder: DataFlowBuilder, name: string) {
    this._builder = builder;
    this._name = name;
  }

  public declare(constructor: ValueConstructor): BuildInfo {
    return this._builder.declare(constructor).name(this._name);
  }

  public define(value: IValue): BuildInfo {
    return this._builder.define(value).name(this._name);
  }

  public bindRaw<T extends IValue>(
    functor: NamedAsyncFunctor<T>,
    constructor: ValueConstructor<T>,
    ...args: Array<BuildInfo>
  ): BuildInfo {
    return this._builder.bindRaw(functor, constructor, ...args).name(this._name);
  }

  public bind<T extends IValue>(
    functor: NamedAsyncFunctor<T>,
    constructor: ValueConstructor<T>,
    ...argsNames: Array<string>
  ): BuildInfo {
    return this._builder.bind(functor, constructor, ...argsNames).name(this._name);
  }
}

export class BuildInfo {
  public namesMap: BuildInfoMap;

  public type: Type;

  public vertex: VertexDescriptor;

  public isInputVertex: boolean;

  public vertexName?: string;

  public constructor(namesMap: BuildInfoMap, type: Type, vertex: VertexDescriptor, isInputVertex: boolean) {
    this.namesMap = namesMap;
    this.type = type;
    this.vertex = vertex;
    this.isInputVertex = isInputVertex;
  }

  public name(name: string): BuildInfo {
    if (name.length === 0) {
      throw new InvalidNameError();
    }
    if (this.namesMap.has(name)) {
      throw new DuplicateNameError();
    }
    this.vertexName = name;
    this.namesMap.set(name, this);
    return this;
  }
}

export type BuildInfoMap = Map<string, BuildInfo>;

export interface GraphInfo {
  graph: Graph;
  namesMap: BuildInfoMap;
}

export class DataFlowBuilder {
  private _graph: Graph;

  private _namesMap: BuildInfoMap;

  private _verticesToCalculate: Set<VertexDescriptor> = new Set();

  private _createdVerticesOrdered: VertexDescriptorCollection = [];

  public constructor(graph: Graph, namesMap: BuildInfoMap) {
    this._graph = graph;
    this._namesMap = namesMap;
  }

  public name(name: string): DataFlowBuilderNamingProxy {
    return new DataFlowBuilderNamingProxy(this, name);
  }

  public existing(name: string): BuildInfo {
    const info = this._namesMap.get(name);
    if (info === undefined) {
      throw new UnknownNameError(name);
    }
    return info;
  }

  private _addVertex(calculator: IValueCalculator, inputsOrder: VertexDescriptorCollection): VertexDescriptor {
    const descriptor = this._graph.addVertex(calculator, inputsOrder);
    this._createdVerticesOrdered.push(descriptor);
    return descriptor;
  }

  private _declare(type: Type, calculator: IValueCalculator): BuildInfo {
    const vertex = this._addVertex(calculator, []);
    return new BuildInfo(this._namesMap, type, vertex, true);
  }

  private _define(type: Type, calculator: IValueCalculator): BuildInfo {
    const buildInfo = this._declare(type, calculator);
    this._verticesToCalculate.add(buildInfo.vertex); // we will have to calculate the newly built vertex so that it will get value from InputValueCalculator
    return buildInfo;
  }

  private _bind<T extends IValue>(
    calculatorFactory: (inpProto: TypeCollection, resProto: Type) => IValueCalculator,
    constructor: ValueConstructor<T>,
    ...args: Array<BuildInfo>
  ): BuildInfo {
    const inpTypes: Array<Type> = [];
    const inpVertices: Array<VertexDescriptor> = [];
    let hasToBeCalculated = false;
    args.forEach((info: BuildInfo): void => {
      inpTypes.push(info.type);
      inpVertices.push(info.vertex);
      if (!hasToBeCalculated) {
        // This case is required for algorithm extensions cases, so when we are extending already built and calculated Graph
        if (this._graph.getValue(info.vertex) !== undefined) {
          hasToBeCalculated = true; // we will have to calculate the newly built vertex if it is dependent on vertex with value
        }
      }
    });
    const resType = constructor.Type;
    const calculator = calculatorFactory(inpTypes, resType);
    const vertex = this._addVertex(calculator, inpVertices);
    if (hasToBeCalculated) {
      this._verticesToCalculate.add(vertex);
    }
    return new BuildInfo(this._namesMap, resType, vertex, false);
  }

  public declare(constructor: ValueConstructor): BuildInfo {
    const type = constructor.Type;
    const calculator = new InputValueCalculator(type);
    return this._declare(type, calculator);
  }

  public define(value: IValue): BuildInfo {
    const type = value.type;
    const calculator = new InputValueCalculator(type, new TypedValue(type, value));
    return this._define(type, calculator);
  }

  public bindRaw<T extends IValue>(
    functor: NamedAsyncFunctor<T>,
    constructor: ValueConstructor<T>,
    ...args: Array<BuildInfo>
  ): BuildInfo {
    return this._bind(
      (inpProto: TypeCollection, resProto: Type) => new FunctorValueCalculator(functor, inpProto, resProto),
      constructor,
      ...args
    );
  }

  public bind<T extends IValue>(
    functor: NamedAsyncFunctor<T>,
    constructor: ValueConstructor<T>,
    ...argsNames: Array<string>
  ): BuildInfo {
    const args = argsNames.map((name: string): BuildInfo => {
      return this.existing(name);
    });
    return this.bindRaw(functor, constructor, ...args);
  }

  // Algorithm building API
  //   finalizes algorithm building process
  //   by performing DAG check, initializing vertices with values
  // Returns: list of the created vertices in order of creation
  public async buildAsync(): Promise<VertexDescriptorCollection> {
    if (!this._graph.isDAG()) throw new CycleDetectedError();
    const dependencies = this._graph.topologicalSort(Array.from(this._verticesToCalculate));
    await this._graph.calculateVerticesAsync(dependencies);
    return this._createdVerticesOrdered;
  }
}
